/**
 * First-run explainer for the Enter Tasks view. Shown ONCE (localStorage-gated)
 * the first time a user opens Tasks, and re-openable any time via the ⓘ button
 * in the Tasks header. Explains what Tasks are and the two setup requirements —
 * a connected Enter integration and the login-email ↔ Enter-user-email match —
 * so an empty list is understood rather than mistaken for a bug.
 *
 * Self-contained (react + lucide + cn only) so it ports verbatim web↔desktop.
 */

import { useCallback, useEffect, useState } from 'react';
import { ListChecks, Plug, UserCheck, X } from 'lucide-react';
import { cn } from '../utils/cn';

const SEEN_KEY = 'enter-tasks-onboarding-seen-v1';

/**
 * First-open (once) visibility + a manual re-show, persisted in localStorage.
 * `autoShow` (default true) drives the once-on-first-open behaviour for the
 * dedicated Tasks tab; pass `false` in secondary surfaces (e.g. the RTM patient
 * snapshot) where only the ⓘ re-show is wanted, not an auto-popup.
 */
export function useTasksOnboarding(autoShow = true) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!autoShow) return;
    try {
      if (typeof window !== 'undefined' && !window.localStorage.getItem(SEEN_KEY)) {
        setOpen(true);
      }
    } catch {
      // localStorage unavailable (private mode, etc.) — just don't auto-show.
    }
  }, [autoShow]);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(SEEN_KEY, 'true');
    } catch {
      /* ignore */
    }
  }, []);

  const show = useCallback(() => setOpen(true), []);

  return { open, show, dismiss };
}

function Step({
  n,
  icon,
  children,
}: {
  n: number;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-2.5">
      <span className="flex items-center justify-center w-6 h-6 shrink-0 rounded-md bg-primary/10 text-primary">
        {icon}
      </span>
      <p className="text-[12px] leading-snug text-muted-foreground">
        <span className="font-medium text-foreground">{n}. </span>
        {children}
      </p>
    </li>
  );
}

export function EnterTasksOnboarding({
  open,
  onDismiss,
  onConnectIntegration,
}: {
  open: boolean;
  onDismiss: () => void;
  onConnectIntegration?: () => void;
}) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="How Tasks work"
      className="fixed inset-0 z-30 flex items-center justify-center bg-background/80 p-3"
    >
      <div className="w-full max-w-[320px] max-h-full overflow-y-auto rounded-lg border border-border bg-card text-card-foreground">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-border/40">
          <span className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/15 text-primary shrink-0">
            <ListChecks size={15} />
          </span>
          <span className="text-[13px] font-semibold text-foreground">How Tasks work</span>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="ml-auto p-1 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          <p className="text-[12px] leading-snug text-foreground/90">
            Your live RCM worklist. Enter automatically assigns billing &amp; coding tasks to your
            team — fix them in your EMR, then mark them done here (that closes them in Enter).
          </p>

          <p className="text-[11px] font-medium text-muted-foreground">
            To see your tasks, two things must be set up:
          </p>

          <ul className="flex flex-col gap-2.5">
            <Step n={1} icon={<Plug size={13} />}>
              Your clinic's <span className="text-foreground">Enter integration</span> is connected —
              an admin adds it in Settings → Integrations.
            </Step>
            <Step n={2} icon={<UserCheck size={13} />}>
              Your Ctrl Enter{' '}
              <span className="text-foreground">login email matches your Enter user email</span> —
              that's how tasks assigned to you show up here. If this list stays empty, ask your admin
              to check your Enter user.
            </Step>
          </ul>

          <div className="flex items-center justify-end gap-2 pt-1">
            {onConnectIntegration && (
              <button
                type="button"
                onClick={onConnectIntegration}
                className={cn(
                  'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border',
                  'border-border text-muted-foreground hover:bg-muted/40 transition-colors'
                )}
              >
                <Plug size={11} /> Connect Enter
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                'inline-flex items-center text-[11px] px-3 py-1 rounded-md',
                'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
              )}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
